/**
 * Greeting Agent
 * 
 * Entry point agent responsible ONLY for:
 * - Trilingual greeting (Arabic/English/Urdu)
 * - Language detection from user response
 * - Transitioning to Router Agent once language is detected
 * 
 * NOTE: System instructions and tools are defined in backend (backend/config/agents.py)
 * This file only contains tool handlers, guardrails, and metadata.
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { noEntryRequirements, languageDetectedGuardrail } from '../guardrails';

/**
 * Create the Greeting Agent node
 * System instructions and tools are provided by backend via ephemeral token
 */
export function createGreetingAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'greeting_agent',
    name: 'Greeting Agent',
    description: 'Handles initial greeting and language detection',
    
    // Initial prompt to trigger the AI to speak the greeting first
    // Backend provides the actual system instruction via ephemeral token
    initialPrompt: 'Greet user in 3 languages NOW. When they respond, detect language, speak welcome, and CALL set_language_preference. Function is MANDATORY - no transition without it.',
    
    // No requirements to enter - this is the entry point
    entryGuardrails: noEntryRequirements(),
    
    // Exit when language is detected - transition to welcome agent
    exitGuardrails: {
      transitions: new Map([
        ['language_detected', {
          target: { agentId: 'welcome_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
      ]),
      fallback: undefined, // No fallback - must detect language
    },
    
    // Handle the set_language_preference tool call
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {
      if (toolName === 'set_language_preference') {
        const language = args.language as 'ar' | 'en' | 'ur';
        
        console.log('[GreetingAgent] Language detected:', language);
        
        return {
          toolName,
          args,
          result: { 
            success: true, 
            message: `Language set to ${language}` 
          },
          // Trigger transition to welcome agent (not router directly)
          triggersTransition: true,
          transitionTo: 'welcome_agent',
          transitionData: { language },
        };
      }
      
      // Unknown tool
      return {
        toolName,
        args,
        result: { success: false, error: 'Unknown tool' },
      };
    },
    
    onActivate: async (context: AgentContext) => {
      console.log('[GreetingAgent] Activated - ready for language detection');
    },
    
    onDeactivate: async (context: AgentContext) => {
      console.log('[GreetingAgent] Deactivated - language:', context.transitionData.language);
    },
  };
}

export default createGreetingAgent;

